import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import Header from '../components/Layout/Header/Header'
import MainContent from '../components/Layout/MainContent/MainContent'
import { LuxBreadcrumb } from '@lux/wardrobe-lib'
import './NewVirtualStorePackage.scss'
import './NewActivity.scss'
import '../components/Gallery/Gallery.scss'
import {
    getAlbumDetails,
    setAlbumDetails,
    setAlbumDetailsError,
    setAlbumDetailsSuccess,
} from '../redux/actions/albums'
import { layoutActions } from '@Techedge/digitalportal-shared'
import { DragDrop } from '../components/VirtualStore/DragDropImages/DragDrop'
import { CtaGroupWrapper } from '../components/UI/CtaGroup/CtaGroupWrapper'
import {
    NewActivityCreationCta,
    NewActivityCreationCtaConfig,
} from '../models/new-activity-creation-state'
import { GalleryService } from '../services/GalleryService'
import DialogConfirmDeletePhotoFromPackage from '../components/VirtualStore/DialogConfirmDeletePhotoFromPackage'
import DialogConfirmPublishPackage from '../components/VirtualStore/DialogConfirmPublishPackage'
import {
    DispatchFunctionProps,
    DispatchToPropsComponent,
    setReloadParentData,
} from '../redux/actions/common'

const VIRTUAL_STORE_NAMESPACE = 'virtualStore'

const VirtualStoreActions = {
    CANCEL: `${VIRTUAL_STORE_NAMESPACE}_cancel`,
    SAVE_AS_DRAFT: `${VIRTUAL_STORE_NAMESPACE}_save_as_draft`,
    PUBLISH: `${VIRTUAL_STORE_NAMESPACE}_publish`,
    SAVE_CHANGES: `${VIRTUAL_STORE_NAMESPACE}_changes`,
}

const NewVirtualStorePackage = (props: {
    storeId: string
    storeName: string
    photoList?: { id: string; url: string }[]
    closeModal: () => void
    hideModal: () => void
    setLoading: (loading: boolean) => void
    setReloadParentData: DispatchToPropsComponent<DispatchFunctionProps>
    setAlbumDetailsError: (error: any) => void
    setAlbumDetailsSuccess: (text: any) => void
}) => {
    const {
        storeName,
        storeId,
        closeModal,
        hideModal,
        setLoading,
        setReloadParentData,
        setAlbumDetailsError,
        setAlbumDetailsSuccess,
    } = props
    const [images, setImages] = useState([])
    const [deletePhotoId, setDeletePhotoId] = useState(null)
    const [status, setStatus] = useState(null)
    const [showConfirmPublishDialog, setShowConfirmPublishDialog] = useState(false)

    const getPackagePhotoList = () => {
        setLoading(true)
        GalleryService.GetPackagePhotoList(storeId)
            .then((res) => {
                setStatus(res.data.status)
                setImages(
                    res.data.photoList.map((p) => ({
                        id: p.attachmentId,
                        url: p.url,
                    }))
                )
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (storeId) {
            getPackagePhotoList()
        }
    }, [])

    const intl = useIntl()

    const componentClass = 'smart-new-vs smart-modals-new-activity'

    const activityBreadcrumb = {
        id: '0',
        label: intl.formatMessage({
            id: 'ph2.gallery.albums.virtual_stores',
            defaultMessage: 'Virtual Store',
        }),
        url: '/activity',
        className: 'activity selectable',
    }

    const createBreadcrumb = {
        id: '1',
        label: intl.formatMessage({
            id: 'modals.new.activity.breadcrumbs.create',
            defaultMessage: 'Create',
        }),
        disabled: true,
    }

    const onCloseModal = () => {
        if (window.location.pathname.indexOf('virtual_store') === -1)
            window.location.href = `${window.location.origin}/gallery/albums/virtual_stores`
        closeModal()
    }

    const onAddImage = () => {
        hideModal()
    }

    const onRemoveImage = (photoId: string) => setDeletePhotoId(photoId)

    const onConfirmDelete = () => {
        setDeletePhotoId(null)
        setLoading(true)
        GalleryService.DeletePhotoByPackage({ storeId: storeId, photoId: deletePhotoId })
            .then(() => {
                setAlbumDetailsSuccess(
                    intl.formatMessage({
                        id: 'ph2.modals.new.package.cta.done',
                        defaultMessage: 'Operation done successfully!',
                    })
                )
                getPackagePhotoList()
            })
            .catch(() => setLoading(false))
    }
    const onSaveChanges = (images) => {
        setImages(images)
        setLoading(true)
        GalleryService.SaveChangesPackage({
            storeId: storeId,
            photoIdList: images.map((i) => i.id),
        })
            .then(() => {
                setReloadParentData()
                setLoading(false)
                setAlbumDetailsSuccess(
                    intl.formatMessage({
                        id: 'ph2.modals.new.package.cta.done',
                        defaultMessage: 'Operation done successfully!',
                    })
                )
            })
            .catch((err) => {
                setLoading(false)
                setAlbumDetailsError(err.response.data.message)
            })
    }
    const onSaveAsDraft = () => {
        setLoading(true)
        GalleryService.SaveAsDraftPackage({ storeId: storeId })
            .then(() => {
                setReloadParentData()
                setAlbumDetailsSuccess(
                    intl.formatMessage({
                        id: 'ph2.modals.new.package.cta.done',
                        defaultMessage: 'Operation done successfully!',
                    })
                )
                getPackagePhotoList()
            })
            .catch((err) => {
                setLoading(false)
                setAlbumDetailsError(err.response.data.message)
            })
    }
    const onPublish = () => {
        setShowConfirmPublishDialog(false)
        setLoading(true)
        GalleryService.PublishPackage({ storeId: storeId })
            .then(() => {
                setReloadParentData()
                setAlbumDetailsSuccess(
                    intl.formatMessage({
                        id: 'ph2.modals.new.package.cta.done',
                        defaultMessage: 'Operation done successfully!',
                    })
                )
                getPackagePhotoList()
            })
            .catch((err) => {
                setLoading(false)
                setAlbumDetailsError(err.response.data.message)
            })
    }

    const ctaConfig = () => {
        const right = []
        right.push(
            new NewActivityCreationCta({
                label: 'modals.virtual.store.cta.save.as.draft',
                defaultLabel: 'Save as Draft',
                action: VirtualStoreActions.SAVE_AS_DRAFT,
                permission: () => true,
                icon: 'bookmark',
                variant: 'outline',
            })
        )
        // if (status === 'draft')
        right.push(
            new NewActivityCreationCta({
                label: 'modals.virtual.store.cta.publish',
                defaultLabel: 'Publish',
                action: VirtualStoreActions.PUBLISH,
                permission: () => true,
                icon: 'send',
                variant: 'outline',
            })
        )
        return new NewActivityCreationCtaConfig({
            left: [
                new NewActivityCreationCta({
                    label: 'modals.virtual.store.cta.cancel',
                    defaultLabel: 'Cancel',
                    action: VirtualStoreActions.CANCEL,
                    permission: () => true,
                    icon: 'pencil',
                    variant: 'text',
                }),
            ],
            right,
        })
    }

    const ctaHandler = (action: string) => {
        switch (action) {
            case VirtualStoreActions.SAVE_AS_DRAFT: {
                onSaveAsDraft()
                return
            }
            case VirtualStoreActions.PUBLISH: {
                setShowConfirmPublishDialog(true)
                return
            }
            case VirtualStoreActions.CANCEL: {
                closeModal()
            }
        }
    }

    const ctaTemplate = useMemo(
        () => (
            <CtaGroupWrapper
                ctaConfig={ctaConfig()}
                componentClass={componentClass}
                ctaHandler={ctaHandler}
            />
        ),
        [status]
    )

    return (
        <div className="smart-modals-fullscreen">
            <div className={componentClass}>
                <div className={`${componentClass}__header`}>
                    <Header onClose={onCloseModal}>
                        <LuxBreadcrumb pathArray={[activityBreadcrumb, createBreadcrumb]} />
                        <h1 className={`${componentClass}__header-mobile`}>
                            {intl.formatMessage({
                                id: 'ph2.modals.new.package',
                                defaultMessage: 'Create a New Package',
                            })}
                        </h1>
                    </Header>
                </div>
                <div className={`${componentClass}__main`}>
                    <div className={`${componentClass}__content pt-5 md:pt-0 w-100`}>
                        <MainContent footer={ctaTemplate} classes="new-vs-main-content">
                            <div className="flex align-items-center">
                                <span className="mandatory">*</span>
                                <span className="bold">{`${storeId} - ${storeName}`}</span>
                            </div>
                            <div className="pt-4">
                                <DragDrop
                                    images={images}
                                    onSetImages={onSaveChanges}
                                    onAddImage={onAddImage}
                                    onRemoveImage={onRemoveImage}
                                />
                            </div>
                        </MainContent>
                    </div>
                </div>
            </div>
            <DialogConfirmDeletePhotoFromPackage
                storeName={storeName}
                visible={deletePhotoId}
                onHide={() => setDeletePhotoId(null)}
                onConfirm={onConfirmDelete}
            />
            <DialogConfirmPublishPackage
                storeName={storeName}
                visible={showConfirmPublishDialog}
                onHide={() => setShowConfirmPublishDialog(false)}
                onConfirm={onPublish}
            />
        </div>
    )
}

const mapStateToProps = (state: any) => ({
    storeId: state.layoutReducer.modals.props?.id,
    storeName: state.layoutReducer.modals.props?.storeName,
    photoList: state.layoutReducer.modals.props?.photoList,
})

const mapDispatchToProps = (dispatch: any) => ({
    setAlbumDetails: (data: any) => dispatch(setAlbumDetails(data)),
    getAlbumDetails: (id: string) => dispatch(getAlbumDetails(id)),
    closeModal: () => {
        dispatch(
            layoutActions.setModals({
                type: null,
                props: { id: null, storeName: null, photoList: null, packageId: null },
            })
        )
    },
    hideModal: () => {
        dispatch(
            layoutActions.setModals({
                type: null,
                props: {},
            })
        )
    },
    setLoading: (loading: boolean) => dispatch(layoutActions.setModals({ props: { loading } })),
    setReloadParentData: () => setReloadParentData({ dispatch }),
    setAlbumDetailsError: (error: any) => dispatch(setAlbumDetailsError(error)),
    setAlbumDetailsSuccess: (text?: string) => dispatch(setAlbumDetailsSuccess(text)),
})

export default connect(mapStateToProps, mapDispatchToProps)(NewVirtualStorePackage)
